import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';

const release = 'facebook-diagnostics-v1';
const idPattern = /^(?:[a-f0-9]{32}|[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})$/i;
const errorTypes = new Set(['Error', 'TypeError', 'ReferenceError', 'RangeError', 'SyntaxError', 'SecurityError', 'AbortError', 'NetworkError', 'AxiosError']);
let installedClient;

function randomId() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
}

export function diagnosticReason(error) {
  const message = String(error?.message || '');
  if (/popup blocked/i.test(message)) return 'popup_blocked';
  if (/window closed/i.test(message)) return 'window_closed';
  if (/timed out/i.test(message) || error?.name === 'AbortError') return 'timeout';
  if (/declined or denied/i.test(message)) return 'authorization_denied';
  if (/did not return an authorization code/i.test(message)) return 'missing_code';
  if (/session changed/i.test(message)) return 'session_changed';
  if (/sign in/i.test(message)) return 'auth_required';
  return error?.response?.status ? 'http_error' : error?.isAxiosError ? 'network_error' : 'javascript_error';
}

export function createDiagnostics({ getUser, send, apiBase, now = Date.now, makeId = randomId,
  getPage = () => 'other', getBrowser = () => ({ browser: 'Other', mobile: false }) }) {
  const queue = [], seen = new Map();
  let pending, windowStart = now(), count = 0, active;
  const base = String(apiBase || '').replace(/\/+$/, '');
  function operation() {
    return active && active.user === getUser() && now() - active.startedAt < 10 * 60000 ? active.id : undefined;
  }
  function beginOperation() {
    const id = makeId();
    active = { id, user: getUser(), startedAt: now() };
    return id;
  }
  function apiEndpoint(config) {
    try {
      const root = new URL(`${base}/`), url = new URL(config.url, root);
      if (url.origin !== root.origin || !url.pathname.startsWith(root.pathname)) return null;
      const endpoint = url.pathname.slice(root.pathname.length);
      return /^[A-Za-z][A-Za-z0-9_-]{0,99}$/.test(endpoint) && endpoint !== 'clientDiagnostics-report' ? endpoint : null;
    } catch (_) { return null; }
  }
  function report(input, expectedUser = getUser()) {
    if (!base || !expectedUser?.uid || expectedUser !== getUser()) return Promise.resolve();
    if (now() - windowStart >= 60000) { count = 0; windowStart = now(); }
    const key = [input.source, input.event, input.operationId, input.endpoint, input.stage, input.reason, input.httpStatus, input.errorType,
      input.asset, input.line, input.column].join('|');
    if (count >= 40 || queue.length >= 20 || (seen.has(key) && now() - seen.get(key) < 10000)) return Promise.resolve();
    if (seen.size >= 128) seen.clear();
    seen.set(key, now()); count++;
    // Only explicitly selected fields cross the network. Never spread Error/config/body objects.
    const event = { release, eventId: makeId(), source: input.source, event: input.event,
      page: getPage(), ...getBrowser() };
    for (const key of ['operationId', 'requestId', 'endpoint', 'stage', 'reason', 'httpStatus', 'errorType', 'durationMs', 'accountCount', 'asset', 'line', 'column']) {
      if (input[key] !== undefined) event[key] = input[key];
    }
    queue.push({ event, user: expectedUser });
    return flush();
  }
  function flush() {
    if (!pending) {
      pending = Promise.resolve().then(async () => {
        while (queue.length) {
          const item = queue.shift();
          if (getUser() !== item.user) continue;
          try {
            const token = await item.user.getIdToken();
            if (getUser() !== item.user) continue;
            await send(`${base}/clientDiagnostics-report?shopName=${encodeURIComponent(item.user.uid)}`, {
              method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify(item.event),
            });
          } catch (_) { /* Diagnostics must never interrupt or retry the merchant operation. */ }
        }
      }).finally(() => {
        pending = undefined;
        if (queue.length) flush();
      });
    }
    return pending;
  }
  function request(config) {
    const endpoint = apiEndpoint(config);
    if (!endpoint) return config;
    const requestId = makeId();
    const headers = config.headers || (config.headers = {});
    const operationId = endpoint.startsWith('facebook-') ? operation() : undefined;
    headers['X-XProfit-Request-ID'] = requestId;
    if (operationId) headers['X-XProfit-Operation-ID'] = operationId;
    config.xprofitDiagnostic = { requestId, operationId, endpoint, startedAt: now(), user: getUser() };
    return config;
  }
  function apiError(error) {
    const context = error?.config?.xprofitDiagnostic;
    if (!context) return;
    const responseId = error.response?.headers?.['x-xprofit-request-id'];
    return report({ source: 'api', event: 'request_failed', stage: 'request', reason: diagnosticReason(error),
      errorType: errorTypes.has(error.name) ? error.name : 'Error', endpoint: context.endpoint,
      requestId: idPattern.test(responseId || '') ? responseId : context.requestId, operationId: context.operationId,
      httpStatus: error.response?.status, durationMs: now() - context.startedAt }, context.user);
  }
  function runtimeError(error, source) {
    const frame = String(error?.stack || '').match(/\/js\/((?:app|chunk-vendors|[0-9]+)\.[a-f0-9]{6,64}\.js):(\d+):(\d+)/);
    return report({ source, event: 'runtime_error', stage: 'runtime', reason: diagnosticReason(error),
      errorType: errorTypes.has(error?.name) ? error.name : 'Error', operationId: operation(),
      ...(frame ? { asset: frame[1], line: Number(frame[2]), column: Number(frame[3]) } : {}) });
  }
  return { report, beginOperation, operation, request, apiError, runtimeError };
}

export function reportDiagnostic(event) {
  try { installedClient?.report(event); } catch (_) { /* Best effort. */ }
}
export function beginFacebookDiagnostic() {
  try { return installedClient?.beginOperation(); } catch (_) { return undefined; }
}

// Shared fetch services use the same metadata as Axios without consuming bodies
// or changing the Response/Error observed by their callers.
export async function diagnosticFetch(url, options = {}) {
  let config;
  try { config = installedClient?.request({ url, headers: { ...options.headers } }); } catch (_) { /* Best effort. */ }
  const requestOptions = config?.xprofitDiagnostic ? { ...options, headers: config.headers } : options;
  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok && config?.xprofitDiagnostic) {
      try { installedClient.apiError({ name: 'Error', config, response: { status: response.status,
        headers: { 'x-xprofit-request-id': response.headers?.get?.('x-xprofit-request-id') } } }); } catch (_) { /* Best effort. */ }
    }
    return response;
  } catch (error) {
    try { installedClient?.apiError({ name: error?.name, isAxiosError: true, config }); } catch (_) { /* Preserve failure. */ }
    throw error;
  }
}

export function installDiagnostics({ Vue, router, store, axios }) {
  if (installedClient) return;
  const getUser = () => firebase.auth().currentUser;
  installedClient = createDiagnostics({ getUser, apiBase: process.env.VUE_APP_FUNCTIONS_URL,
    getPage: () => {
      const path = router.currentRoute?.path || '';
      return path.startsWith('/setup') || path.startsWith('/settings') ? 'setup'
        : path === '/' ? 'dashboard' : path.includes('onboarding') ? 'onboarding' : path.includes('analytics') ? 'analytics' : 'other';
    },
    getBrowser: () => {
      const agent = window.navigator.userAgent;
      return { browser: /Edg\//.test(agent) ? 'Edge' : /Firefox\//.test(agent) ? 'Firefox'
        : /Chrome\//.test(agent) ? 'Chrome' : /Safari\//.test(agent) ? 'Safari' : 'Other', mobile: /Mobile|Android/.test(agent) };
    },
    send: async (url, options) => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 5000);
      try { await window.fetch(url, { ...options, signal: controller.signal, cache: 'no-store' }); }
      finally { clearTimeout(timer); }
    },
  });
  const runtimeError = (error, source) => {
    try { installedClient.runtimeError(error, source); } catch (_) { /* Best effort. */ }
  };
  const previousHandler = Vue.config.errorHandler;
  Vue.config.errorHandler = (error, vm, info) => {
    runtimeError(error, 'vue');
    if (previousHandler) previousHandler(error, vm, info);
    else console.error(error);
  };
  window.addEventListener('error', event => runtimeError(event.error, 'window'));
  window.addEventListener('unhandledrejection', event => runtimeError(event.reason, 'promise'));
  axios.interceptors.request.use(config => {
    try { return installedClient.request(config); } catch (_) { return config; }
  });
  axios.interceptors.response.use(response => response, error => {
    try { installedClient.apiError(error); } catch (_) { /* Preserve the original API error. */ }
    return Promise.reject(error);
  });
  store.subscribeAction({ error: (action, state, error) => runtimeError(error, 'vue') });
}
